/**
 * MapProvider — React context wrapping a MapLibre GL instance.
 *
 * Provides the map instance to child components via useMap() hook,
 * replacing ad-hoc mapRef patterns across the codebase.
 */

import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import type { Map as MapLibreMap, MapOptions } from 'maplibre-gl';

export interface MapContextValue {
  /** The MapLibre GL map instance (null until loaded) */
  map: MapLibreMap | null;
  /** Whether the map style has finished loading */
  isLoaded: boolean;
  /** Error message if map failed to initialize */
  error: string | null;
  /** The map container DOM element ref */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const MapContext = createContext<MapContextValue | null>(null);

export interface MapProviderProps {
  children: ReactNode;
  /** MapLibre map options (excluding container, which is managed internally) */
  options?: Omit<MapOptions, 'container'>;
  /** CSS class for the map container div */
  className?: string;
  /** Callback when map is loaded */
  onLoad?: (map: MapLibreMap) => void;
}

export function MapProvider({ children, options, className, onLoad }: MapProviderProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<MapLibreMap | null>(null);
  const [mapState, setMapState] = useState<MapLibreMap | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stabilize options and onLoad via refs to avoid re-initializing the map
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    if (!containerRef.current || mapInstanceRef.current) return;

    let cancelled = false;

    // Dynamic import to avoid SSR issues
    import('maplibre-gl').then((maplibregl) => {
      if (cancelled || !containerRef.current) return;

      const map = new maplibregl.default.Map({
        container: containerRef.current,
        ...optionsRef.current,
      });

      mapInstanceRef.current = map;

      map.on('load', () => {
        if (!cancelled) {
          setMapState(map);
          setIsLoaded(true);
          onLoadRef.current?.(map);
        }
      });
    }).catch((err) => {
      if (!cancelled) {
        const message = err instanceof Error ? err.message : 'Failed to load maplibre-gl';
        console.error('[MapProvider] Failed to load maplibre-gl:', err);
        setError(message);
      }
    });

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setMapState(null);
        setIsLoaded(false);
      }
    };
  }, []);

  const contextValue: MapContextValue = {
    map: mapState,
    isLoaded,
    error,
    containerRef,
  };

  return (
    <MapContext.Provider value={contextValue}>
      <div ref={containerRef} className={className} style={{ width: '100%', height: '100%' }} />
      {children}
    </MapContext.Provider>
  );
}

export function useMap(): MapContextValue {
  const ctx = useContext(MapContext);
  if (!ctx) {
    throw new Error('useMap must be used within a <MapProvider>');
  }
  return ctx;
}
